import {
  handleRetrieveBlogComments,
  handleRetrieveBlogs,
} from "../../../redux/guest-slices/common.slice";
import { useCallback, useEffect, useRef, useState } from "react";
import { debounce, FILE_PATH_URI } from "../../../utils/helper";
import { useDispatch, useSelector } from "react-redux";
import CommentA from "../../ui/CommentA";
import { uid } from "uid";

import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  Grid2 as Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";

function BlogComments() {
  const listRef = useRef();
  const dispatch = useDispatch();
  const [data, setdata] = useState({});
  const [comments, setComments] = useState([]);
  const [commentId, setCommentId] = useState("");
  const [reply, setReply] = useState({ id: null, view: false });
  const { loadings, refresh } = useSelector((state) => state.otherSlice);

  // Handle get all blogs (User added comments)
  const handleretrieveAll = async (page) => {
    const listElement = listRef.current;
    const scrollTop = listElement?.scrollTop;

    const response = await dispatch(
      handleRetrieveBlogs({
        limit: 10,
        currentPage: page || null,
      })
    ).unwrap();

    setdata((prev) => ({
      pagination: response?.pagination,
      records:
        page === 1 || !page
          ? response?.records
          : [...prev?.records, ...response?.records],
    }));
    if (commentId === "") {
      setCommentId(
        page === 1 || !page
          ? response?.records?.[0]?._id
          : data?.records?.[0]?._id
      );
    }

    setTimeout(() => {
      if (listElement) listElement.scrollTop = scrollTop;
    }, 0);
  };

  // eslint-disable-next-line
  const handleDebouncedFetch = useCallback(
    debounce(() => {
      if (!loadings?.retrieveAll && data?.pagination?.hasNextPage) {
        handleretrieveAll(data?.pagination?.currentPage + 1);
      }
    }, 300),
    // eslint-disable-next-line
    [loadings?.retrieveAll, data?.pagination, handleretrieveAll]
  );

  // Fetch comments when commentId changes
  useEffect(() => {
    const fetchComments = async () => {
      if (commentId) {
        const { records } = await dispatch(
          handleRetrieveBlogComments({
            limit: 100,
            id: commentId,
          })
        ).unwrap();
        setComments(records);
      }
    };
    fetchComments();
  }, [commentId, dispatch, refresh]);

  useEffect(() => {
    const listElement = listRef.current;

    const handleScroll = () => {
      if (
        listElement.scrollTop + listElement.clientHeight >=
          listElement.scrollHeight &&
        !loadings?.retrieveAll &&
        data?.pagination?.hasNextPage
      ) {
        handleDebouncedFetch();
      }
    };

    listElement.addEventListener("scroll", handleScroll);
    return () => listElement.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line
  }, [handleDebouncedFetch]);

  useEffect(() => {
    if (refresh) {
      setReply({ id: null, view: false });
    }
    handleretrieveAll();

    // eslint-disable-next-line
  }, [refresh]);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: "auto" }}>
        <Paper
          sx={{
            p: 1,
            gap: 1,
            height: "100%",
            display: "flex",
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "divider",
            flexDirection: "column",
            transition: "all 450ms ease",
            boxShadow: "0 0 10px 0 #0000000a",
          }}
        >
          <Stack
            spacing={1}
            ref={listRef}
            sx={{
              minWidth: { md: "350px" },
              maxHeight: "600px",
              overflowY: "auto",
            }}
          >
            {loadings?.retrieveAll ? (
              new Array(3).fill().map((_, i) => (
                <Skeleton
                  key={i}
                  sx={{
                    width: "100%",
                    height: "60px",
                    transform: "none",
                  }}
                />
              ))
            ) : data?.records?.length === 0 ? (
              <Alert
                sx={{
                  fontSize: "20px",
                  fontWeight: "500",
                  alignItems: "center",
                  fontFamily: "Poppins",
                  ".MuiAlert-message": { p: "0" },
                  ".MuiAlert-icon": { fontSize: "28px" },
                }}
                color="error"
                severity="error"
              >
                No Records Found!
              </Alert>
            ) : (
              data?.records?.map((element, i) => (
                <Box
                  key={i}
                  sx={{
                    p: 1,
                    gap: 2,
                    display: "flex",
                    cursor: "pointer",
                    borderRadius: "6px",
                    transition: "all ease 350ms",
                    bgcolor: commentId === element?._id ? "#00000010" : "",
                    ":hover": { bgcolor: "#00000010" },
                  }}
                  onClick={() => {
                    setCommentId(element?._id);
                  }}
                >
                  <Avatar
                    sx={{ width: 50, height: 50 }}
                    src={FILE_PATH_URI + element?.banner?.path}
                  />
                  <Box
                    sx={{
                      width: "100%",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <Typography
                      width="100%"
                      fontWeight="600"
                      overflow="hidden"
                      whiteSpace="nowrap"
                      color="other.color1"
                      textOverflow="ellipsis"
                      textTransform="capitalize"
                    >
                      {element?.title}
                    </Typography>
                    <Typography
                      fontSize={14}
                      fontWeight="400"
                      color="other.color2"
                      textTransform="capitalize"
                    >
                      Author: {element?.author?.fullName}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
            {loadings?.retrieveAll && (
              <Box sx={{ textAlign: "center", pb: 1 }}>
                <CircularProgress size={30} thickness={4} />
              </Box>
            )}
          </Stack>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, md: "grow" }}>
        <Paper
          sx={{
            p: 1,
            gap: 1,
            height: "100%",
            display: "flex",
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "divider",
            flexDirection: "column",
            transition: "all 450ms ease",
            boxShadow: "0 0 10px 0 #0000000a",
          }}
        >
          {loadings?.retrieveSingle || loadings?.retrieveAll ? (
            new Array(3).fill().map((_, i) => (
              <Skeleton
                key={i}
                sx={{
                  width: "100%",
                  height: "50px",
                  transform: "none",
                }}
              />
            ))
          ) : comments?.length === 0 ? (
            <Alert
              sx={{
                fontSize: "20px",
                fontWeight: "500",
                alignItems: "center",
                fontFamily: "Poppins",
                ".MuiAlert-message": { p: "0" },
                ".MuiAlert-icon": { fontSize: "28px" },
              }}
              color="error"
              severity="error"
            >
              No Records Found!
            </Alert>
          ) : (
            comments?.map((comment) => (
              <CommentA
                commnetType="commentEdit"
                key={uid(6)}
                reply={reply}
                setReply={setReply}
                comment={comment}
              />
            ))
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}

export default BlogComments;
